import { useEffect } from "react";

/**
 * Swap the document's PWA manifest + apple-mobile-web-app-title at runtime.
 *
 * iOS Safari and Android Chrome both read these from the `<head>` at the
 * moment the user invokes "Add to Home Screen". Without this hook, the
 * employee portal would install the admin PWA (start_url `/`) instead of
 * `/dolgozo` because only one manifest is referenced in index.html.
 *
 * Usage: drop ``<UsePwaManifest href="/manifest-portal.json" title="SIPX Dolgozó" />``
 * once inside a route's top-level component.
 */
export function usePwaManifest({ href, title }) {
  useEffect(() => {
    if (typeof document === "undefined") return;
    const head = document.head;

    // Manifest link
    let manifestLink = head.querySelector('link[rel="manifest"]');
    if (!manifestLink) {
      manifestLink = document.createElement("link");
      manifestLink.rel = "manifest";
      head.appendChild(manifestLink);
    }
    const prevHref = manifestLink.getAttribute("href");
    manifestLink.setAttribute("href", href);

    // Apple home-screen title
    let appleTitle = head.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (!appleTitle) {
      appleTitle = document.createElement("meta");
      appleTitle.name = "apple-mobile-web-app-title";
      head.appendChild(appleTitle);
    }
    const prevTitle = appleTitle.getAttribute("content");
    if (title) appleTitle.setAttribute("content", title);

    // Restore on unmount so leaving the portal back into admin restores admin PWA
    return () => {
      if (prevHref) manifestLink.setAttribute("href", prevHref);
      if (prevTitle) appleTitle.setAttribute("content", prevTitle);
    };
  }, [href, title]);
}

/** Convenience zero-DOM component */
export default function UsePwaManifest({ href, title }) {
  usePwaManifest({ href, title });
  return null;
}
